import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAssignSource } from "@/api/reseller-manager/mutations";
import { ResellerSelect } from "@/components/shared/reseller-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	selectedSns: string[];
};

export function AssignToResellerDialog({
	open,
	onOpenChange,
	selectedSns,
}: Props) {
	const { t } = useTranslation();
	const [resellerId, setResellerId] = useState<number | null>(null);

	const assign = useAssignSource();

	useEffect(() => {
		if (open) setResellerId(null);
	}, [open]);

	function handleSubmit() {
		if (resellerId === null) return;
		assign.mutate(
			{ reseller_id: resellerId, mqtt_source_sns: selectedSns },
			{
				onSuccess: () => {
					toast.success(t("mqttSources.assignSuccess"));
					onOpenChange(false);
				},
				onError: () => {
					toast.error(t("mqttSources.assignError"));
				},
			},
		);
	}

	const canSubmit =
		resellerId !== null && selectedSns.length > 0 && !assign.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Przypisz do resellera</DialogTitle>
					<DialogDescription>
						Wybierz resellera dla zaznaczonych bramek
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="selected-gateways">
							Zaznaczone bramki ({selectedSns.length})
						</Label>
						<div className="flex flex-wrap gap-1.5">
							{selectedSns.map((sn) => (
								<Badge key={sn} variant="secondary">
									{sn}
								</Badge>
							))}
						</div>
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="reseller-select">Reseller</Label>
						<ResellerSelect value={resellerId} onChange={setResellerId} />
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={assign.isPending}
					>
						Anuluj
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{assign.isPending ? "Przypisywanie…" : "Przypisz"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
